import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import SharedHeader from "@/components/SharedHeader";
import SharedFooter from "@/components/SharedFooter";
import SEO, { breadcrumbSchema } from "@/components/SEO";
import { API } from "@/App";
import {
  BookOpen,
  Calendar,
  Clock,
  ArrowRight,
  Sparkles,
  GraduationCap,
  Lightbulb,
  Calculator,
  FlaskConical,
  BookText,
  Globe2,
  RefreshCw,
  Archive,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

// Subject icons mapping
const subjectIcons = {
  mathematics: Calculator,
  science: FlaskConical,
  english: BookText,
  hindi: BookOpen,
  social_studies: Globe2,
  general: Lightbulb,
};

// Subject colors mapping
const subjectColors = {
  mathematics: "bg-blue-100 text-blue-700 border-blue-200",
  science: "bg-green-100 text-green-700 border-green-200",
  english: "bg-purple-100 text-purple-700 border-purple-200",
  hindi: "bg-orange-100 text-orange-700 border-orange-200",
  social_studies: "bg-amber-100 text-amber-700 border-amber-200",
  general: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

// Blog Card Component (defined outside to avoid re-creation on render)
function BlogCard({ post, featured = false, onClick }) {
  const SubjectIcon = subjectIcons[post.subject] || BookOpen;
  const colorClass = subjectColors[post.subject] || subjectColors.general;
  
  return (
    <Card 
      className={`hover:shadow-lg transition-shadow cursor-pointer group ${featured ? 'border-emerald-200' : ''}`}
      onClick={onClick}
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between mb-2">
          <Badge className={`${colorClass} border`}>
            <SubjectIcon className="w-3 h-3 mr-1" />
            {post.subject?.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}
          </Badge>
          <span className="text-xs text-gray-500 flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {new Date(post.created_at).toLocaleDateString("en-IN", { 
              day: "numeric", 
              month: "short" 
            })}
          </span>
        </div>
        <CardTitle className={`${featured ? 'text-xl' : 'text-lg'} group-hover:text-emerald-600 transition-colors line-clamp-2`}>
          {post.title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-gray-600 text-sm line-clamp-3 mb-4">
          {post.excerpt || post.content?.substring(0, 150)}...
        </p>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {post.read_time || "1 min"} read
          </span>
          <span className="text-emerald-600 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
            Read more
            <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

// Archive Item Component
function ArchiveItem({ post, onClick }) {
  const SubjectIcon = subjectIcons[post.subject] || BookOpen;
  const colorClass = subjectColors[post.subject] || subjectColors.general;
  
  return (
    <div
      onClick={onClick}
      className="flex items-start gap-3 p-4 bg-white border rounded-lg hover:shadow-md transition-all cursor-pointer group"
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass.replace('text-', 'bg-').split(' ')[0]}`}>
        <SubjectIcon className={`w-5 h-5 ${colorClass.split(' ')[1]}`} />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-gray-900 line-clamp-1 group-hover:text-emerald-600 transition-colors">
          {post.title}
        </h4>
        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {new Date(post.created_at).toLocaleDateString("en-IN", { 
              day: "numeric", 
              month: "short",
              year: "numeric"
            })}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {post.read_time || "1 min"}
          </span>
        </div>
      </div>
      <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-emerald-600 transition-colors flex-shrink-0" />
    </div>
  );
}

export default function Blog() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubject, setSelectedSubject] = useState("all");
  const [showArchive, setShowArchive] = useState(false);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await fetch(`${API}/blog/posts?limit=50`);
      if (response.ok) {
        const data = await response.json();
        setPosts(data.posts || []);
      }
    } catch (error) {
      console.error("Error fetching posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPosts = selectedSubject === "all" 
    ? posts 
    : posts.filter(post => post.subject === selectedSubject);

  // Split into latest 3 and archive
  const latestPosts = filteredPosts.slice(0, 3);
  const archivedPosts = filteredPosts.slice(3);

  // SEO structured data
  const blogStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Blog",
        "name": "Eduzy Learning Blog",
        "description": "Daily AI-generated educational articles for students in grades 5-8. Learn interesting facts about Math, Science, English, and Hindi.",
        "url": "https://eduzy.co/blog",
        "publisher": {
          "@type": "Organization",
          "name": "Eduzy"
        }
      },
      breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Blog", url: "/blog" }
      ])
    ]
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <SEO
        title="Learning Blog - Daily Educational Articles | Grades 5-8 | Eduzy"
        description="Read daily AI-generated educational articles for students. Interesting facts, fun learning, and study tips for Math, Science, English, and Hindi."
        keywords="educational blog, learning articles, student blog, CBSE tips, math facts, science facts, study tips, grades 5-8"
        canonicalUrl="/blog"
        structuredData={blogStructuredData}
      />

      <SharedHeader />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-emerald-50 via-white to-teal-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 rounded-full px-4 py-1.5 mb-3">
            <Sparkles className="w-4 h-4" />
            <span className="font-medium text-sm">AI-Generated Daily Learning</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
            Eduzy Learning Blog
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto mb-4">
            Fresh educational content every day! Our AI generates interesting articles on Math, Science, English, and Hindi to keep you learning something new.
          </p>
          
          {/* Subject Filter */}
          <div className="flex flex-wrap justify-center gap-2 mb-4">
            <Button
              variant={selectedSubject === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedSubject("all")}
              className={selectedSubject === "all" ? "bg-emerald-600" : ""}
            >
              All Subjects
            </Button>
            {Object.entries(subjectIcons).map(([subject, Icon]) => (
              <Button
                key={subject}
                variant={selectedSubject === subject ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedSubject(subject)}
                className={selectedSubject === subject ? "bg-emerald-600" : ""}
              >
                <Icon className="w-4 h-4 mr-1" />
                {subject.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Posts Section */}
      <section className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 text-emerald-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Loading articles...</p>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No articles yet</h3>
              <p className="text-gray-600 mb-6">Check back soon for fresh educational content!</p>
            </div>
          ) : (
            <>
              {/* Latest 3 Posts */}
              <div className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-emerald-600" />
                  Latest Articles
                </h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {latestPosts.map((post) => (
                    <BlogCard 
                      key={post.id} 
                      post={post} 
                      featured 
                      onClick={() => navigate(`/blog/${post.slug}`)}
                    />
                  ))}
                </div>
              </div>

              {/* Archive Section */}
              {archivedPosts.length > 0 && (
                <div className="border-t pt-8">
                  <button
                    onClick={() => setShowArchive(!showArchive)}
                    className="w-full flex items-center justify-between p-4 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors mb-4"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                        <Archive className="w-5 h-5 text-gray-600" />
                      </div>
                      <div className="text-left">
                        <h3 className="font-semibold text-gray-900">Archive</h3>
                        <p className="text-sm text-gray-500">{archivedPosts.length} older {archivedPosts.length === 1 ? 'article' : 'articles'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <span className="text-sm font-medium">{showArchive ? 'Hide' : 'View'}</span>
                      {showArchive ? (
                        <ChevronUp className="w-5 h-5" />
                      ) : (
                        <ChevronDown className="w-5 h-5" />
                      )}
                    </div>
                  </button>

                  {showArchive && (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 animate-in slide-in-from-top-2 duration-300">
                      {archivedPosts.map((post) => (
                        <ArchiveItem 
                          key={post.id} 
                          post={post} 
                          onClick={() => navigate(`/blog/${post.slug}`)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-emerald-600 to-teal-600">
        <div className="max-w-3xl mx-auto text-center">
          <GraduationCap className="w-12 h-12 text-white/80 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-4">
            Want Personalized Learning?
          </h2>
          <p className="text-emerald-100 mb-6">
            Our AI tutor adapts to your learning style and pace. Try EduWise for personalized lessons!
          </p>
          <Button
            onClick={() => navigate("/eduwise")}
            size="lg"
            className="bg-white text-emerald-600 hover:bg-gray-100"
          >
            Explore EduWise AI
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      <SharedFooter />
    </div>
  );
}
